/**
 * User-friendly error messages for frontend
 */

export const ERROR_MESSAGES = {
  // Network Errors
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection and try again.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  SERVER_ERROR: 'Server error occurred. Our team has been notified. Please try again later.',
  
  // Authentication Errors
  AUTH_EXPIRED: 'Your session has expired. Please sign in again.',
  AUTH_REQUIRED: 'Please sign in to continue.',
  AUTH_INVALID: 'Authentication failed. Please sign in again.',
  
  // Payment Errors
  PAYMENT_FAILED: 'Payment failed. Please try again or use a different payment method.',
  PAYMENT_CANCELLED: 'Payment was cancelled.',
  PAYMENT_ERROR: 'An error occurred during payment processing. Please try again.',
  
  // Booking Errors
  BOOKING_UNAVAILABLE: 'This time slot is no longer available. Please select a different time.',
  BOOKING_FAILED: 'Unable to complete booking. Please try again.',
  CART_EMPTY: 'Your cart is empty.',
  
  // Form Validation
  VALIDATION_ERROR: 'Please check the form and fix any errors.',
  REQUIRED_FIELDS: 'Please fill in all required fields.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_PHONE: 'Please enter a valid phone number.',
  INVALID_DATE: 'Please select a valid date.',
  
  // Upload Errors
  UPLOAD_FAILED: 'File upload failed. Please try again.',
  UPLOAD_TOO_LARGE: 'File is too large. Maximum size is 10MB.',
  UPLOAD_INVALID_TYPE: 'Invalid file type. Please upload an image.',
  
  // Generic Errors
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
  NOT_FOUND: 'The requested item was not found.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  
  // User Actions
  CANCEL_FAILED: 'Unable to cancel. Please try again or contact support.',
  UPDATE_FAILED: 'Unable to save changes. Please try again.',
  DELETE_FAILED: 'Unable to delete. Please try again.',
  CREATE_FAILED: 'Unable to create. Please try again.',
};

/**
 * Get user-friendly error message from API error
 */
export function getErrorMessage(error: unknown): string {
  const axiosError = error as { response?: { data?: { error?: string }; status?: number }; message?: string; code?: string };
  // Check if error has a response from the API
  if (axiosError?.response?.data?.error) {
    return axiosError.response.data.error;
  }
  
  // Handle network errors
  if (axiosError?.message === 'Network Error' || !axiosError?.response) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }
  
  // Handle timeout
  if (axiosError?.code === 'ECONNABORTED') {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }
  
  // Handle HTTP status codes
  if (axiosError?.response?.status) {
    switch (axiosError.response.status) {
      case 401:
        return ERROR_MESSAGES.AUTH_EXPIRED;
      case 403:
        return ERROR_MESSAGES.FORBIDDEN;
      case 404:
        return ERROR_MESSAGES.NOT_FOUND;
      case 500:
      case 502:
      case 503:
      case 504:
        return ERROR_MESSAGES.SERVER_ERROR;
      default:
        break;
    }
  }
  
  // Check error message for known patterns
  if (axiosError?.message) {
    const message = axiosError.message.toLowerCase();
    
    if (message.includes('network')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }
    if (message.includes('auth') || message.includes('token')) {
      return ERROR_MESSAGES.AUTH_EXPIRED;
    }
  }
  
  // Default fallback
  return axiosError?.message || ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Error types for different scenarios
 */
export const ERROR_TYPES = {
  NETWORK: 'network',
  AUTH: 'auth',
  VALIDATION: 'validation',
  SERVER: 'server',
  UNKNOWN: 'unknown',
} as const;

export type ErrorType = typeof ERROR_TYPES[keyof typeof ERROR_TYPES];

/**
 * Determine error type
 */
export function getErrorType(error: unknown): ErrorType {
  const axiosError = error as { response?: { status?: number }; message?: string; code?: string };
  if (!axiosError?.response && (axiosError?.message === 'Network Error' || axiosError?.code === 'ECONNABORTED')) {
    return ERROR_TYPES.NETWORK;
  }
  
  const status = axiosError?.response?.status;
  if (status === 401 || status === 403) {
    return ERROR_TYPES.AUTH;
  }
  
  if (status === 400 || status === 422) {
    return ERROR_TYPES.VALIDATION;
  }
  
  if (status && status >= 500) {
    return ERROR_TYPES.SERVER;
  }
  
  return ERROR_TYPES.UNKNOWN;
}

